import { cable } from "@hotwired/turbo-rails"
import { camelizeObjKeys } from "../lib/collection_helpers"
import { fetchJSON } from "../lib/fetch"

import QueryMeta from "./query_meta"
import QueryResult from "./query_result"

// Note: If you change this, you must also update the `DEFAULT_QUERY` constant
// in app/controllers/insights/queries_controller.rb
export const DEFAULT_QUERY = "fields @ts, @preview\n| sort @ts"

/**
 * Manage and distribute query results
 */
class Query {
  constructor() {
    this.queries = {};
    this.subscriptions = {};
    this.target = new EventTarget();
  }

  async fetch({query, timezone, ts, querySessionId, streamIds, path = "/search", source}) {
    if (query === "" || query === null) {
      this.queryEmpty(querySessionId)
      return
    }

    this.queryStart(querySessionId)

    const response = await fetchJSON({
      camelizeResponseBody: true,
      path,
      timeout: 60000,
      jsonBody: { query, timezone, querySessionId, ts, stream_ids: streamIds, source },
    })

    const meta = new QueryMeta(response.meta);

    if(response.error) {
      this.queryError(querySessionId, { response, meta })
    } else {
      // Group with index
      const results = response.results.map(r => new QueryResult(r, meta))
      this.queryReady(querySessionId, { response, meta, results })
    }

    return response
  }

  async progressSubscribe({querySessionId, channelName, streamName}) {
    this.subscriptions[querySessionId] = await cable.subscribeTo({
      channel: channelName,
      signed_stream_name: streamName
    }, {
      received: this.onProgressMessage.bind(this)
    })
  }

  onProgressMessage(data) {
    const message = camelizeObjKeys(JSON.parse(data))

    this.emit("progress", message.querySessionId, message)
  }

  queryEmpty(querySessionId) {
    this.queries[querySessionId] = { status: "empty" }
    this.emit("empty", querySessionId, {query: null})
  }

  queryStart(querySessionId) {
    const query = this.queries[querySessionId] = {
      startedAt: Date.now(),
      status: "start"
    }

    this.emit("start", querySessionId, query)
  }

  queryError(querySessionId, data) {
    const query = this.queries[querySessionId] = {
      ...this.queries[querySessionId],
      errorAt: Date.now(),
      status: "error",
      data
    }

    this.emit("error", querySessionId, query)
  }

  queryReady(querySessionId, data) {
    const query = this.queries[querySessionId] = {
      ...this.queries[querySessionId],
      readyAt: Date.now(),
      status: "ready",
      data
    }

    this.emit("ready", querySessionId, query)
  }

  query(querySessionId) {
    return this.queries[querySessionId]
  }

  // This is a bit of a hack to trigger the current query status when a new
  // component mounts
  triggerCurrent(querySessionId, callbacks) {
    const query = this.query(querySessionId);
    if(query) {
      const evt = new CustomEvent(`${query.status}-${querySessionId}`, { detail: { query }, cancelable: true })
      if(callbacks[query.status]) callbacks[query.status](evt)
    }
  }

  on(event, querySessionId, listener) {
    return this.target.addEventListener(
      `${event}-${querySessionId}`, listener
    );
  }

  off(event, querySessionId, listener) {
    return this.target.removeEventListener(
      `${event}-${querySessionId}`,
      listener
    );
  }

  emit(event, querySessionId, query) {
    return this.target.dispatchEvent(
      new CustomEvent(`${event}-${querySessionId}`, { detail: { query }, cancelable: true })
    );
  }
}

const qcInstance = new Query();
Object.freeze(qcInstance);
export default qcInstance
